import React from 'react';
import dayjs from 'dayjs';
import { useParams } from 'react-router';
import { Typography } from 'antd';
import type { UnionExpand } from '@owl-lib/type-util';
import Spinner from '../../../assets/app-spinner.svg';
import { Locale } from '../../../context/AppProvider';
import { ClaimDetailsContext, Params } from '../ClaimDetailsContext';
import useFilesHook from '../FilesListContainer/useFilesHook';
import ClaimFileViewer from './ClaimFileViewer';

interface Props {
  fetchFileContent?(data: {
    dossierId: string;
    fileId: string;
    startPage: number;
    endPage: number;
  }): void;
  fileId: string;
  currPage: number;
  setCurrPage(page: number): void;
  totalPages: number;
  inDrawer: boolean;
}

export const TTL_EXPIRY_SECONDS = 30;
export const PAGE_RANGE = 10;
export const isPageStale = (requestedDate: number) =>
  dayjs().diff(requestedDate, 'seconds') > TTL_EXPIRY_SECONDS;

const ClaimFileViewerContainer: React.FC<Props> = ({
  fetchFileContent,
  fileId,
  currPage,
  setCurrPage,
  totalPages,
  inDrawer,
}) => {
  const { dossierId } = useParams() as UnionExpand<Params>;
  const { messages } = React.useContext(Locale);
  const { claimMetadata } = React.useContext(ClaimDetailsContext);
  const [
    {
      filesContent,
      actions: { fetchFileContents },
    },
  ] = useFilesHook(dossierId);
  const fetchFileContentsStatus = fetchFileContents?.status;

  React.useEffect(() => {
    if (!filesContent || !fetchFileContent || !claimMetadata?.dossier_id) {
      return;
    }

    let startPage: number | null = null;
    let endPage: number | null = null;

    for (
      let i = Math.max(1, currPage - PAGE_RANGE);
      i <= Math.min(totalPages, currPage + PAGE_RANGE);
      i++
    ) {
      const page = filesContent[fileId]?.[i];
      if (!page || isPageStale(page.requestedDate)) {
        if (startPage === null) {
          startPage = i;
          endPage = i;
        } else {
          startPage = Math.min(startPage, i);
          endPage = Math.max(endPage as number, i);
        }
      }
    }
    if (startPage !== null && endPage !== null) {
      fetchFileContent({
        dossierId: claimMetadata?.dossier_id,
        fileId,
        startPage,
        endPage,
      });
    }
  }, [
    fetchFileContent,
    fileId,
    filesContent,
    currPage,
    totalPages,
    claimMetadata,
  ]);

  if (
    !filesContent?.[fileId]?.[currPage] &&
    fetchFileContentsStatus === 'pending'
  ) {
    return (
      <div className="app-spinner">
        <Spinner />
      </div>
    );
  }

  if (
    !filesContent?.[fileId]?.[currPage] &&
    fetchFileContentsStatus === 'rejected'
  ) {
    return (
      <Typography.Text>
        {messages['claim_overview.files.view.error']}
      </Typography.Text>
    );
  }

  return (
    <ClaimFileViewer
      fileUrls={Object.fromEntries(
        Object.entries(filesContent?.[fileId] || {}).map(([key, value]) => [
          parseInt(key, 10),
          value.url,
        ])
      )}
      totalPages={totalPages}
      currPage={currPage}
      setCurrPage={setCurrPage}
    />
  );
};

export default ClaimFileViewerContainer;
